import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Plus, X, MapPin, User, Clock, AlertTriangle } from "lucide-react";
import api, { formatErr } from "../lib/apiClient";
import { fmtDate } from "../lib/format";
import CustomerPicker from "../components/CustomerPicker";

const PRIORITY_COLORS = {
  low: "#10B981",
  medium: "#0F4C81",
  high: "#EF4444",
};
const STATUS_COLORS = {
  Scheduled: "#0F4C81",
  "In Progress": "#F59E0B",
  Completed: "#10B981",
  Cancelled: "#94a3b8",
};

export default function Schedule() {
  const nav = useNavigate();
  const calRef = useRef();
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("dayGridMonth");

  const blank = {
    customer_id: "", service_address: "", scheduled_start: "", scheduled_end: "",
    estimated_duration_minutes: 60, job_type: "Service Call",
    priority: "medium", status: "Scheduled", notes: "",
    assigned_employee_ids: [], materials: [],
  };

  const load = async () => {
    const [{ data: js }, { data: cs }, { data: es }] = await Promise.all([
      api.get("/jobs?include_closed=true"),
      api.get("/customers"),
      api.get("/employees"),
    ]);
    setJobs(js); setCustomers(cs); setEmployees(es);
  };
  useEffect(() => { load(); }, []);

  const events = jobs.map((j) => {
    const cust = customers.find((c) => c.id === j.customer_id);
    return {
      id: j.id,
      title: `${cust?.name || "Unknown"} — ${j.job_type || "Job"}`,
      start: j.scheduled_start,
      end: j.scheduled_end || undefined,
      backgroundColor: STATUS_COLORS[j.status] || "#0F4C81",
      borderColor: STATUS_COLORS[j.status] || "#0F4C81",
      extendedProps: j,
    };
  });

  const openCreate = (start) => {
    setEditing({ ...blank, scheduled_start: start });
    setShowForm(true);
  };

  const onDateClick = (info) => {
    // info.dateStr could be 'YYYY-MM-DD' or full ISO
    const d = new Date(info.dateStr);
    if (info.allDay) d.setHours(9, 0, 0, 0);
    openCreate(d.toISOString());
  };

  const onEventDrop = async (info) => {
    try {
      await api.patch(`/jobs/${info.event.id}/schedule`, {
        scheduled_start: info.event.start?.toISOString(),
        scheduled_end: info.event.end?.toISOString() || null,
      });
    } catch (e) {
      info.revert();
      alert(formatErr(e));
    }
  };

  const onEventClick = (info) => {
    nav(`/jobs/${info.event.id}`);
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...editing, estimated_duration_minutes: Number(editing.estimated_duration_minutes) };
      await api.post("/jobs", payload);
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div data-testid="schedule-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">{jobs.length} job{jobs.length !== 1 ? "s" : ""} on the books · drag to reschedule</p>
        </div>
        <button
          data-testid="new-job-btn"
          onClick={() => { setEditing({ ...blank, scheduled_start: new Date(Date.now() + 60 * 60 * 1000).toISOString() }); setShowForm(true); }}
          className="self-start px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"
        >
          <Plus size={16} /> Schedule Job
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={view}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          height="auto"
          events={events}
          editable={true}
          selectable={true}
          dateClick={onDateClick}
          eventClick={onEventClick}
          eventDrop={onEventDrop}
          eventResize={onEventDrop}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          eventTimeFormat={{ hour: "numeric", minute: "2-digit", meridiem: "short" }}
          dayMaxEvents={3}
          firstDay={1}
        />
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={save} data-testid="job-form" className="bg-white rounded-md border border-slate-200 max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center"><h2 className="font-display text-xl font-bold">Schedule Job</h2><button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button></div>
            {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Customer *</label>
                <div className="mt-1">
                  <CustomerPicker
                    testid="job-customer"
                    value={editing.customer_id}
                    onChange={(cid, c) => setEditing({
                      ...editing,
                      customer_id: cid,
                      service_address: c?.service_addresses?.[0] || c?.billing_address || editing.service_address || "",
                    })}
                    required
                    customers={customers}
                  />
                </div>
              </div>
              <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Service Address</label>
                <input data-testid="job-address" value={editing.service_address} onChange={(e) => setEditing({ ...editing, service_address: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
              </div>
              <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Job Type</label>
                <input data-testid="job-type" value={editing.job_type} onChange={(e) => setEditing({ ...editing, job_type: e.target.value })} placeholder="e.g. Drain repair" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
              </div>
              <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Priority</label>
                <select data-testid="job-priority" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
              <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Start *</label>
                <input data-testid="job-start" type="datetime-local" value={editing.scheduled_start?.slice(0, 16) || ""} onChange={(e) => setEditing({ ...editing, scheduled_start: new Date(e.target.value).toISOString() })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
              </div>
              <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Estimated Duration (min)</label>
                <input data-testid="job-duration" type="number" value={editing.estimated_duration_minutes} onChange={(e) => setEditing({ ...editing, estimated_duration_minutes: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] tabular" />
              </div>
              <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Assign Employees</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {employees.length === 0 && <span className="text-xs text-slate-500">No employees yet. Add some in Employees page.</span>}
                  {employees.map((emp) => {
                    const checked = editing.assigned_employee_ids.includes(emp.id);
                    return (
                      <label key={emp.id} className={`px-3 py-1.5 text-xs rounded border cursor-pointer ${checked ? "bg-[#E0F2FE] border-[#0F4C81] text-[#0F4C81]" : "border-slate-300 text-slate-700"}`}>
                        <input type="checkbox" className="hidden" checked={checked} onChange={() => {
                          const ids = checked ? editing.assigned_employee_ids.filter((x) => x !== emp.id) : [...editing.assigned_employee_ids, emp.id];
                          setEditing({ ...editing, assigned_employee_ids: ids });
                        }} />
                        {emp.name}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Notes</label>
                <textarea data-testid="job-notes" value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={5} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" data-testid="save-job-btn" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Schedule</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
